import { useCallback, useState } from "react";
import { ApiError, postRawJson } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { API_OFFBOARDING_INITIATE } from "../../../../../config/Endpoints.js";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_OFFBOARDING_URL, normalizeSqeOffboarding, unwrapSqeOffboardingResponse } from "../api/sqe-offboarding-api.ts";
import { OffboardingRequest, OffboardingRequestSchema } from "../schemas/index.ts";

interface UseOffboardingInitiateResult {
    initiate: (retentionRequested: boolean) => Promise<OffboardingRequest | null>;
    loading: boolean;
    error: string | null;
}

export function useOffboardingInitiate(): UseOffboardingInitiateResult {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSqe = useSqeLabs();

    const initiate = useCallback(async (retentionRequested: boolean) => {
        setLoading(true);
        setError(null);

        try {
            const { data } = await postRawJson<unknown>(
                isSqe ? `${SQE_OFFBOARDING_URL}/initiate` : API_OFFBOARDING_INITIATE,
                accessToken(),
                retentionRequested,
            );

            const parse = OffboardingRequestSchema.safeParse(isSqe ? normalizeSqeOffboarding(unwrapSqeOffboardingResponse(data)) : data);
            if (!parse.success) {
                setError("Unexpected response from server.");
                return null;
            }

            return parse.data;
        } catch (e) {
            const msg =
                e instanceof ApiError
                    ? e.message
                    : "Failed to initiate offboarding request.";
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    }, [isSqe]);

    return { initiate, loading, error };
}
